/**
 * Create or edit a consent requirement: what clients are asked, and what for.
 *
 * A requirement presents one document and states, in the words the client
 * ticks, what they agree to. Whether it blocks the app (mandatory), whether it
 * can be taken back (withdrawable), who is asked (a service, a channel) and
 * what it covers (a purpose) are all set here — none of it is written into the
 * apps. A mandatory requirement cannot also be withdrawable: withdrawing it
 * would only lock the client out, so the server refuses the pair and the form
 * does not offer it.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

import { keyFrom } from './document-form-dialog';
import {
  instantToLocalInput,
  legalKeys,
  localInputToInstant,
  type DocumentRow,
  type LegalOptions,
  type RequirementRow,
} from './types';

const KEY = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

export function RequirementFormDialog({
  open,
  onClose,
  requirement,
  documents,
  options,
}: {
  open: boolean;
  onClose: () => void;
  /** `null` creates one, as a draft. */
  requirement: RequirementRow | null;
  documents: DocumentRow[];
  options: LegalOptions | undefined;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [kind, setKind] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [statement, setStatement] = useState('');
  const [explanation, setExplanation] = useState('');
  const [mandatory, setMandatory] = useState(true);
  const [withdrawable, setWithdrawable] = useState(false);
  const [enforceOnApi, setEnforceOnApi] = useState(false);
  const [service, setService] = useState('');
  const [channel, setChannel] = useState('');
  const [purpose, setPurpose] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [order, setOrder] = useState('100');

  useEffect(() => {
    if (!open) return;
    setTitle(requirement?.title ?? '');
    setKey(requirement?.key ?? '');
    setKeyTouched(requirement !== null);
    setKind(requirement?.kind ?? options?.requirement_kinds[0]?.value ?? '');
    setDocumentId(requirement?.document_public_id ?? '');
    setStatement(requirement?.statement ?? '');
    setExplanation(requirement?.explanation ?? '');
    setMandatory(requirement?.mandatory ?? true);
    setWithdrawable(requirement?.withdrawable ?? false);
    setEnforceOnApi(requirement?.enforce_on_api ?? false);
    setService(requirement?.service ?? '');
    setChannel(requirement?.channel ?? '');
    setPurpose(requirement?.purpose ?? '');
    setEffectiveAt(instantToLocalInput(requirement?.effective_at ?? null));
    setOrder(String(requirement?.display_order ?? 100));
  }, [open, requirement, options]);

  const save = useMutation({
    mutationFn: () => {
      const fields = {
        title,
        statement,
        explanation: explanation || null,
        document_public_id: documentId,
        mandatory,
        withdrawable: mandatory ? false : withdrawable,
        enforce_on_api: enforceOnApi,
        service: service || null,
        channel: channel || null,
        purpose: purpose || null,
        effective_at: localInputToInstant(effectiveAt),
        display_order: Number(order),
      };
      return requirement
        ? api.patch<RequirementRow>(`/legal/requirements/${requirement.public_id}`, fields)
        : api.post<RequirementRow>('/legal/requirements', { ...fields, key, kind });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      toast.success(
        requirement ? 'Requirement updated' : 'Requirement created',
        requirement ? undefined : 'It is a draft: activate it to start asking clients.',
      );
      onClose();
    },
  });

  const orderValue = Number(order);
  const valid =
    title.trim().length >= 2 &&
    statement.trim().length >= 2 &&
    documentId !== '' &&
    kind !== '' &&
    (requirement !== null || KEY.test(key)) &&
    Number.isInteger(orderValue) &&
    orderValue >= 0 &&
    orderValue <= 10_000;

  // A retired document cannot be newly presented; the one already chosen stays
  // listed so editing an old requirement does not silently change it.
  const documentOptions = documents
    .filter((d) => d.status === 'active' || d.public_id === requirement?.document_public_id)
    .map((d) => ({
      value: d.public_id,
      label: d.current_version ? d.title : `${d.title} (nothing published yet)`,
    }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={requirement ? 'Edit requirement' : 'New requirement'}
      description="What clients are asked to accept or consent to, and when it applies."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
            {requirement ? 'Save' : 'Create draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error instanceof ApiError ? (
          <Alert tone="danger">{save.error.message}</Alert>
        ) : null}
        {requirement?.status === 'active' ? (
          <Alert tone="info">
            It is active. A change applies to clients' next decision; decisions already made keep
            the statement they were shown.
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Title"
            required
            maxLength={160}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (!keyTouched) setKey(keyFrom(event.target.value));
            }}
          />
          <Input
            label="Key"
            required
            maxLength={60}
            value={key}
            disabled={requirement !== null}
            hint="How a feature checks it. Fixed once created."
            onChange={(event) => {
              setKeyTouched(true);
              setKey(event.target.value.toLowerCase());
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Kind"
            required
            value={kind}
            disabled={requirement !== null}
            options={options?.requirement_kinds ?? []}
            onChange={(event) => setKind(event.target.value)}
          />
          <Select
            label="Document"
            required
            placeholder="Choose a document"
            value={documentId}
            options={documentOptions}
            onChange={(event) => setDocumentId(event.target.value)}
          />
        </div>

        <Textarea
          label="Statement"
          required
          rows={2}
          maxLength={500}
          hint="The words the client ticks, recorded with every decision."
          value={statement}
          onChange={(event) => setStatement(event.target.value)}
        />
        <Textarea
          label="Explanation"
          rows={2}
          maxLength={2000}
          hint="Optional: why it is asked, shown under the statement."
          value={explanation}
          onChange={(event) => setExplanation(event.target.value)}
        />

        <div className="space-y-3 rounded-card border border-line p-3">
          <Switch
            label="Mandatory"
            hint={
              mandatory
                ? 'Clients cannot use the app until they accept it.'
                : 'Clients may decline it and keep using the app.'
            }
            checked={mandatory}
            onChange={(event) => setMandatory(event.target.checked)}
          />
          {mandatory ? null : (
            <Switch
              label="Can be withdrawn"
              hint="Clients can take it back later, as easily as they gave it."
              checked={withdrawable}
              onChange={(event) => setWithdrawable(event.target.checked)}
            />
          )}
          <Switch
            label="Applies to API keys"
            hint="Until accepted, the client's API keys are refused too. Off, only the apps wait."
            checked={enforceOnApi}
            onChange={(event) => setEnforceOnApi(event.target.checked)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Service"
            placeholder="Every client"
            value={service}
            options={options?.services ?? []}
            onChange={(event) => setService(event.target.value)}
          />
          <Select
            label="Channel"
            placeholder="Any"
            value={channel}
            options={options?.channels ?? []}
            hint={channel && service === 'messaging' ? 'Asked only of clients on this channel.' : undefined}
            onChange={(event) => setChannel(event.target.value)}
          />
          <Select
            label="Purpose"
            placeholder="None"
            value={purpose}
            options={options?.purposes ?? []}
            onChange={(event) => setPurpose(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            type="datetime-local"
            label="Asked from"
            value={effectiveAt}
            hint="Your local time. Empty: as soon as it is active."
            onChange={(event) => setEffectiveAt(event.target.value)}
          />
          <Input
            type="number"
            label="Order"
            min={0}
            max={10_000}
            value={order}
            hint="Lower comes first on the client's screen."
            onChange={(event) => setOrder(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
